import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, LogOut, Package, Shield, Menu, X, Wrench } from "lucide-react";

const PC_BUILDER_URL = "https://build.interstate-pc.com";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CATS = [
  { slug: "gpus", name: "GPUs" }, { slug: "cpus", name: "CPUs" },
  { slug: "motherboards", name: "Motherboards" }, { slug: "ram", name: "Memory" },
  { slug: "storage", name: "Storage" }, { slug: "monitors", name: "Monitors" },
  { slug: "peripherals", name: "Peripherals" }, { slug: "desktops", name: "Desktops" },
  { slug: "laptops", name: "Laptops" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/catalog?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-zinc-800 bg-[#050505]/85 backdrop-blur-xl">
      <div className="bg-[#FF5C00] text-black text-xs py-1.5 overflow-hidden">
        <div className="container-tight flex items-center justify-center gap-8 font-mono font-medium tracking-wide">
          <span>COMPUTERS · COMPONENTS · TECHNOLOGY</span>
          <span className="hidden md:inline">•</span>
          <span className="hidden md:inline">FOR HOME · BUSINESS · SCHOOL · GAMING</span>
          <span className="hidden md:inline">•</span>
          <span className="hidden md:inline">SALES@INTERSTATE-PC.COM</span>
        </div>
      </div>
      <div className="container-tight">
        <div className="flex items-center gap-6 py-3">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <img src="/interstate-pc-logo.jpg" alt="Interstate PC — Technology & Components Network"
              className="h-11 md:h-12 w-auto"
              style={{ imageRendering: "auto" }} />
          </Link>

          <form onSubmit={onSearch} className="flex-1 max-w-2xl relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search GPUs, CPUs, laptops, mice…"
              className="pl-10 pr-24 h-11 bg-[#121217] border-zinc-800 focus-visible:ring-[#FF5C00] focus-visible:border-[#FF5C00] rounded-sm"
            />
            <Button data-testid="search-submit" type="submit" size="sm"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono text-xs tracking-wider">
              SEARCH
            </Button>
          </form>

          <div className="flex items-center gap-2 ml-auto">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="user-menu-btn" variant="ghost"
                    className="rounded-sm h-11 border border-zinc-800 hover:border-zinc-600 hover:bg-transparent">
                    <User className="w-4 h-4 mr-2" /><span className="hidden md:inline">{user.name?.split(" ")[0] || "Account"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0a0a0c] border-zinc-800 rounded-sm w-56">
                  <DropdownMenuLabel className="font-mono text-xs text-zinc-500">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/account/orders")} className="cursor-pointer">
                    <Package className="w-4 h-4 mr-2" />My Orders
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="cursor-pointer">
                      <Shield className="w-4 h-4 mr-2" />Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }} className="cursor-pointer text-[#FF5C00]">
                    <LogOut className="w-4 h-4 mr-2" />Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="login-btn" onClick={() => nav("/login")} variant="ghost"
                className="rounded-sm h-11 border border-zinc-800 hover:border-zinc-600 hover:bg-transparent">
                <User className="w-4 h-4 mr-2" /><span className="hidden md:inline">Sign In</span>
              </Button>
            )}
            <Button data-testid="cart-btn" onClick={() => nav("/cart")} variant="ghost"
              className="rounded-sm h-11 border border-zinc-800 hover:border-[#FF5C00] hover:bg-transparent relative">
              <ShoppingCart className="w-4 h-4" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#FF5C00] text-black text-xs font-mono font-bold px-1.5 py-0.5 min-w-[20px] text-center rounded-sm">
                  {count}
                </span>
              )}
            </Button>
            <button data-testid="mobile-menu-btn" className="md:hidden h-11 w-11 border border-zinc-800 flex items-center justify-center rounded-sm"
              onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1 py-2 border-t border-zinc-900 -mx-1 overflow-x-auto">
          <NavLink to="/catalog" data-testid="nav-all"
            className={({isActive}) => `font-mono text-xs uppercase tracking-widest px-3 py-2 hover:text-[#FF5C00] ${isActive ? "text-[#FF5C00]" : "text-zinc-400"}`}>
            All Products
          </NavLink>
          {CATS.map((c) => (
            <NavLink key={c.slug} data-testid={`nav-${c.slug}`} to={`/catalog?category=${c.slug}`}
              className="font-mono text-xs uppercase tracking-widest px-3 py-2 text-zinc-400 hover:text-[#FF5C00] whitespace-nowrap">
              {c.name}
            </NavLink>
          ))}
          <a href={PC_BUILDER_URL} data-testid="nav-pc-builder"
            className="font-mono text-xs uppercase tracking-widest px-3 py-2 text-[#00F0FF] hover:text-[#FF5C00] whitespace-nowrap flex items-center gap-1.5 ml-1">
            <Wrench className="w-3.5 h-3.5" />PC Builder
          </a>
        </nav>

        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-2 border-t border-zinc-900 pt-3">
            <form onSubmit={onSearch} className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search…" className="pl-10 bg-[#121217] border-zinc-800 rounded-sm" />
            </form>
            <NavLink to="/catalog" onClick={() => setMobileOpen(false)}
              className="block font-mono text-xs uppercase tracking-widest px-3 py-2 text-zinc-300 hover:text-[#FF5C00]">
              All Products
            </NavLink>
            {CATS.map((c) => (
              <NavLink key={c.slug} to={`/catalog?category=${c.slug}`} onClick={() => setMobileOpen(false)}
                className="block font-mono text-xs uppercase tracking-widest px-3 py-2 text-zinc-300 hover:text-[#FF5C00]">
                {c.name}
              </NavLink>
            ))}
            <a href={PC_BUILDER_URL} data-testid="nav-pc-builder-mobile" onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-3 py-2 text-[#00F0FF] hover:text-[#FF5C00] border-t border-zinc-900 mt-2 pt-3">
              <Wrench className="w-3.5 h-3.5" />PC Builder
            </a>
          </div>
        )}
      </div>
    </header>
  );
}
